/* Ayudate · Tarjeta de una oferta de tutor (un tutor + un curso puntual)

   Cada tarjeta muestra los datos de ESE curso, no del tutor en general: un
   tutor que da tres cursos tiene tres tarjetas con precio, descripción,
   calificación y sesiones propias de cada uno. Esos tres últimos valores
   ya vienen resueltos por curso desde expandirOfertas() (src/lib/horarios.js),
   que lee statsPorCurso de la hoja "Tutores" y solo cae al dato general del
   tutor si ese curso todavía no tiene calificaciones desglosadas. */

import React, { useState } from "react";
import { infoCurso } from "../datos/areas";
import { colones } from "../lib/formato";
import { Avatar } from "./Avatar";
import { Capa } from "./Capa";
import { api } from "../api";

const POR_PAGINA_RESENAS = 10;

function fechaCorta(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "" : d.toLocaleDateString("es-CR", { day: "numeric", month: "long", year: "numeric" });
}

function ResenasPublicas({ tutorId, curso, nombreCurso, onCerrado }) {
  const [resenas, setResenas] = useState(null);
  const [error, setError] = useState("");
  const [pagina, setPagina] = useState(1);

  React.useEffect(() => {
    let vivo = true;
    api.getResenasPublicas(tutorId, curso)
      .then((r) => vivo && setResenas(r))
      .catch(() => vivo && setError("No pudimos cargar las reseñas. Revise su conexión y pruebe de nuevo."));
    return () => { vivo = false; };
  }, [tutorId, curso]);

  const paginas = Math.max(1, Math.ceil((resenas?.length || 0) / POR_PAGINA_RESENAS));
  const paginaSegura = Math.min(pagina, paginas);
  const visibles = (resenas || []).slice(
    (paginaSegura - 1) * POR_PAGINA_RESENAS,
    paginaSegura * POR_PAGINA_RESENAS
  );

  return (
    <Capa onCerrar={onCerrado} etiqueta={`Reseñas de ${nombreCurso}`}>
      <div className="mdl mdl-ancho">
        <div className="mdl-h">
          <h3>Reseñas de {nombreCurso}</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          {error ? (
            <p className="vacio">{error}</p>
          ) : !resenas ? (
            <p className="vacio">Cargando reseñas…</p>
          ) : !resenas.length ? (
            <p className="vacio">Todavía no hay reseñas para este curso.</p>
          ) : (
            <>
              {visibles.map((r, i) => (
                <div className="fila" key={i}>
                  <div className="fila-info">
                    <span className="rank-rating">
                      <b>{"★".repeat(r.calificacion)}</b>{"☆".repeat(5 - r.calificacion)}
                    </span>
                    <p>{r.estudiante} · {fechaCorta(r.fecha)}</p>
                    {r.comentario && <p className="fila-nota"><b>Comentario:</b> {r.comentario}</p>}
                  </div>
                </div>
              ))}
              {paginas > 1 && (
                <nav className="pag" aria-label={`Páginas de reseñas de ${nombreCurso}`}>
                  <button type="button" className="pbtn" disabled={paginaSegura === 1}
                    onClick={() => setPagina(paginaSegura - 1)} aria-label="Página anterior">‹</button>
                  {Array.from({ length: paginas }, (_, i) => i + 1).map((p) => (
                    <button type="button" key={p} className={`pbtn ${p === paginaSegura ? "on" : ""}`}
                      aria-current={p === paginaSegura ? "page" : undefined}
                      aria-label={`Página ${p}`} onClick={() => setPagina(p)}>{p}</button>
                  ))}
                  <button type="button" className="pbtn" disabled={paginaSegura === paginas}
                    onClick={() => setPagina(paginaSegura + 1)} aria-label="Página siguiente">›</button>
                </nav>
              )}
            </>
          )}
        </div>
      </div>
    </Capa>
  );
}

export function TutorCard({ t, activo, onVerHorarios }) {
  const i = infoCurso(t.curso);
  const [verResenas, setVerResenas] = useState(false);

  return (
    <article id={`tutor-${t.ofertaId}`}
      className={`tut ${t.estado === "pendiente" ? "pend" : ""}`}
      style={{ "--ac": i.color }}>
      <div className="strip" />
      <div className="body">
        {/* Solo se marca lo pendiente. El "Tutor aprobado" que iba acá no
            decía nada: getTutores() ya filtra por aprobado y publicado, así
            que toda tarjeta del catálogo lo está por definición. El estado
            pendiente sí se ve —en la vista previa que abre el tutor desde su
            panel con ?tutorId=— y ahí sí hace falta. */}
        {t.estado === "pendiente" && (
          <div className="flag pendiente">
            <i aria-hidden="true" />
            Pendiente de revisión
          </div>
        )}
        {/* El curso encabeza la tarjeta: es por lo que se busca. Arriba, en
            mono, el código y dónde se imparte; debajo, el nombre completo.
            La nota del tutor en el curso NO aparece — es dato privado (ver
            el aviso de FormTutor.jsx). El nivel sí, pero solo cuando es un
            nivel del Marco Común (idiomas): la expresión regular deja pasar
            "B2" y frena un "92". */}
        <div className={`cursotit ${activo ? "activo" : ""}`}>
          <span className="cursotit-meta">
            {!i.sinCodigo && <b translate="no">{i.cod}</b>}
            <span>{i.instNom || i.areaNom}</span>
            {t.nivel && /^[ABC][12]$/.test(t.nivel) && (
              <span className="nivelt">Nivel {t.nivel}</span>
            )}
          </span>
          <h3>{i.nom}</h3>
        </div>
        <div className="top">
          <Avatar tutor={t} />
          <div>
            <div className="nm">{t.nom}</div>
            <div className="mt">{t.carrera} · {t.anio}</div>
          </div>
        </div>
        <p className="bio">{t.bio}</p>
        {t.estado === "pendiente" && (
          <p className="pendnota">
            Estamos verificando su comprobante de nota. <b>No se pueden solicitar
            tutorías</b> con este tutor hasta que un administrador lo apruebe.
          </p>
        )}
        <div className="stats">
          {t.rating === null && !t.sesiones ? (
            <span className="nuevo">Sin calificaciones todavía</span>
          ) : (
            <>
              {t.rating !== null && (
                <span title={`Promedio de calificación en ${i.nom}`}>
                  <span className="star">★</span> <span className="rt">{t.rating.toFixed(1)}</span>{" "}
                  ({t.resenas})
                </span>
              )}
              {!!t.sesiones && (
                <span className="mono" title={`Sesiones realizadas en ${i.nom}`}>
                  {t.sesiones} sesiones
                </span>
              )}
            </>
          )}
          {/* Acá decía "Virtual" en cada tarjeta. Todas las tutorías lo son,
              así que la palabra no distinguía una de otra: lo dice una vez
              la sección del buscador, arriba del catálogo. */}
        </div>
        <div className="pr">
          <div>
            <div className="amt">{colones(t.tarifaCliente)}</div>
            <span className="u">por hora</span>
          </div>
          <div className="tut-acc">
            <button type="button" className="btn"
              onClick={() => setVerResenas(true)}>
              Ver reseñas
            </button>
            <button className="btn btn-p"
              disabled={t.estado === "pendiente"}
              title={t.estado === "pendiente"
                ? "Este perfil todavía no ha sido aprobado por un administrador"
                : undefined}
              onClick={() => t.estado === "aprobado" && onVerHorarios(t)}>
              {t.estado === "pendiente" ? "No disponible" : "Ver horarios"}
            </button>
          </div>
        </div>
      </div>
      {verResenas && (
        <ResenasPublicas tutorId={t.id} curso={t.curso} nombreCurso={i.nom}
          onCerrado={() => setVerResenas(false)} />
      )}
    </article>
  );
}
